import { useCallback, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Group,
  Loader,
  Modal,
  Stack,
  Text,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { IconAlertCircle } from "@tabler/icons-react";
import { useCasePathologistAssignment } from "../../hooks/useCasePathologistAssignment";
import type {
  AssignmentRecoveryState,
  ReplacementCandidate,
  ReplacePathologistResponse,
  StaleAssignmentConflict,
} from "../../types/casePathologistAssignment";
import { getCasePathologistAssignmentError } from "../../types/casePathologistAssignment";
import { createUuid } from "../../utils/createUuid";
import {
  assignmentErrorGuidance,
  replacementCandidateOptions,
} from "../../utils/casePathologistAssignmentPresentation";
import ReplacePathologistFields from "./ReplacePathologistFields";
import ReplacePathologistModalAlerts from "./ReplacePathologistModalAlerts";
import type { ReplacementFormValues } from "./replacePathologistFormTypes";

interface ReplacePathologistModalProps {
  opened: boolean;
  onClose: () => void;
  caseId: string;
  onReplaced: (response: ReplacePathologistResponse) => void;
}

const initialValues: ReplacementFormValues = {
  replacementPathologistId: "",
  reason: "",
  acknowledgeActiveReview: false,
};

const defaultReplacementReason = "Changed from Case Detail";
const reasonValidationMessage =
  "Enter at least 3 characters, or leave the reason blank.";
const idleRecoveryState: AssignmentRecoveryState = {
  status: "idle",
  canRetry: false,
};

export default function ReplacePathologistModal({
  opened,
  onClose,
  caseId,
  onReplaced,
}: ReplacePathologistModalProps) {
  const {
    canReplacePathologist,
    contextQuery,
    replaceMutation,
    recoveryState = idleRecoveryState,
  } = useCasePathologistAssignment(caseId, { enabled: opened });
  const form = useForm<ReplacementFormValues>({
    initialValues,
    validateInputOnBlur: true,
    validate: {
      replacementPathologistId: (value) =>
        value ? null : "Choose a replacement pathologist.",
      reason: (value) => {
        const reasonLength = value.trim().length;
        return reasonLength === 0 || (reasonLength >= 3 && reasonLength <= 500)
          ? null
          : reasonValidationMessage;
      },
    },
  });
  const [candidateDropdownOpened, setCandidateDropdownOpened] = useState(false);
  const [preservedCandidate, setPreservedCandidate] =
    useState<ReplacementCandidate>();
  const [staleConflict, setStaleConflict] = useState<StaleAssignmentConflict>();
  const [reviewedUpdatedAssignment, setReviewedUpdatedAssignment] =
    useState(false);
  const context = contextQuery.data;

  const availableCandidates = useMemo(() => {
    const candidates = [...(context?.candidates ?? [])];
    if (
      preservedCandidate &&
      !candidates.some((candidate) => candidate.id === preservedCandidate.id)
    ) {
      candidates.push(preservedCandidate);
    }
    return candidates;
  }, [context?.candidates, preservedCandidate]);

  const candidateOptions = useMemo(
    () =>
      replacementCandidateOptions(
        availableCandidates,
        context?.currentAssignment.pathologist.id,
        form.values.replacementPathologistId
      ),
    [availableCandidates, context, form.values.replacementPathologistId]
  );

  const selectedCandidate = useMemo(
    () =>
      availableCandidates.find(
        (candidate) => candidate.id === form.values.replacementPathologistId
      ),
    [availableCandidates, form.values.replacementPathologistId]
  );

  const displayedWarnings = useMemo(
    () => [
      ...(context?.warnings ?? []),
      ...(selectedCandidate?.warnings ?? []),
    ],
    [context?.warnings, selectedCandidate?.warnings]
  );

  const replacementErrorGuidance = useMemo(() => {
    const error = getCasePathologistAssignmentError(replaceMutation.error);
    return error && error.code !== "PATHOLOGIST_ASSIGNMENT_STALE"
      ? assignmentErrorGuidance(error)
      : "Review the current assignment and try again.";
  }, [replaceMutation.error]);

  const canSubmit = useMemo(() => {
    return Boolean(
      context &&
        form.values.replacementPathologistId &&
        (!staleConflict || reviewedUpdatedAssignment) &&
        !replaceMutation.isPending
    );
  }, [
    context,
    form.values.replacementPathologistId,
    reviewedUpdatedAssignment,
    replaceMutation.isPending,
    staleConflict,
  ]);

  const resetAndClose = useCallback(() => {
    setCandidateDropdownOpened(false);
    setPreservedCandidate(undefined);
    setStaleConflict(undefined);
    setReviewedUpdatedAssignment(false);
    form.reset();
    onClose();
  }, [form, onClose]);

  const handleCandidateMouseDown = useCallback(
    (event: React.MouseEvent<HTMLInputElement>) => {
      if (event.detail === 0) {
        setCandidateDropdownOpened(true);
      }
    },
    []
  );

  const handleCandidateDropdownOpen = useCallback(() => {
    setCandidateDropdownOpened(true);
  }, []);

  const handleCandidateDropdownClose = useCallback(() => {
    setCandidateDropdownOpened(false);
  }, []);

  const handleReviewedUpdatedAssignmentChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setReviewedUpdatedAssignment(event.currentTarget.checked);
    },
    []
  );

  const handleSubmit = useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      const validation = form.validate();
      if (
        validation.hasErrors ||
        !context ||
        !canSubmit ||
        replaceMutation.isPending
      ) {
        return;
      }

      if (
        context.activeReview.acknowledgementRequired &&
        !form.values.acknowledgeActiveReview
      ) {
        form.setFieldError(
          "acknowledgeActiveReview",
          "Acknowledge the active review before continuing."
        );
        return;
      }

      setPreservedCandidate(selectedCandidate);
      try {
        const response = await replaceMutation.mutateAsync({
          requestId: createUuid(),
          expectedCurrentPathologistId:
            context.currentAssignment.pathologist.id,
          expectedAssignmentRevision: context.currentAssignment.revision,
          replacementPathologistId: form.values.replacementPathologistId,
          reason: form.values.reason.trim() || defaultReplacementReason,
          acknowledgeActiveReview: form.values.acknowledgeActiveReview,
        });
        onReplaced(response);
        resetAndClose();
      } catch (error) {
        const assignmentError = getCasePathologistAssignmentError(error);
        if (assignmentError?.code !== "PATHOLOGIST_ASSIGNMENT_STALE") {
          return;
        }

        setStaleConflict(assignmentError);
        setReviewedUpdatedAssignment(false);
        form.setFieldValue("acknowledgeActiveReview", false);
        await contextQuery.refetch();
      }
    },
    [
      canSubmit,
      context,
      contextQuery,
      form,
      onReplaced,
      replaceMutation,
      resetAndClose,
      selectedCandidate,
    ]
  );

  return (
    <Modal
      opened={opened}
      onClose={resetAndClose}
      closeOnClickOutside={false}
      title="Replace pathologist"
      size="lg"
      centered
      styles={{
        content: {
          maxWidth: "calc(100vw - var(--mantine-spacing-md) * 2)",
          maxHeight: "calc(100dvh - var(--mantine-spacing-md) * 2)",
        },
        body: { overflowY: "auto" },
      }}
    >
      {contextQuery.isLoading ? (
        <Group justify="center" py="xl">
          <Loader aria-label="Loading reassignment context" />
        </Group>
      ) : contextQuery.isError ? (
        <Alert
          icon={<IconAlertCircle size={18} />}
          color="red"
          variant="light"
          title="Unable to load reassignment details"
          role="alert"
        >
          Close this window and try again.
        </Alert>
      ) : !canReplacePathologist ? (
        <Alert
          icon={<IconAlertCircle size={18} />}
          color="red"
          variant="light"
          title="Permission required"
          role="alert"
        >
          You do not have permission to replace the case pathologist.
        </Alert>
      ) : context ? (
        <form onSubmit={handleSubmit}>
          <Stack gap="md">
            <Stack gap={2}>
              <Text size="sm" c="dimmed">
                Case {context.caseNumber}
              </Text>
              <Text fw={600}>
                Current pathologist:{" "}
                {context.currentAssignment.pathologist.fullName}
              </Text>
              <Text size="sm" c="dimmed">
                {context.lifecycleStatus}
              </Text>
            </Stack>

            <ReplacePathologistModalAlerts
              context={context}
              staleConflict={staleConflict}
              recoveryState={recoveryState}
              warnings={displayedWarnings}
            />

            <ReplacePathologistFields
              form={form}
              candidateOptions={candidateOptions}
              candidateDropdownOpened={candidateDropdownOpened}
              onCandidateMouseDown={handleCandidateMouseDown}
              onCandidateDropdownOpen={handleCandidateDropdownOpen}
              onCandidateDropdownClose={handleCandidateDropdownClose}
              activeReviewAcknowledgementRequired={
                context.activeReview.acknowledgementRequired
              }
              staleConflictActive={Boolean(staleConflict)}
              reviewedUpdatedAssignment={reviewedUpdatedAssignment}
              onReviewedUpdatedAssignmentChange={
                handleReviewedUpdatedAssignmentChange
              }
            />

            {replaceMutation.error &&
              !staleConflict &&
              recoveryState.status === "idle" && (
                <Alert
                  icon={<IconAlertCircle size={18} />}
                  color="red"
                  variant="light"
                  title="Pathologist could not be replaced"
                  role="alert"
                >
                  {replacementErrorGuidance}
                </Alert>
              )}

            <Group justify="flex-end">
              <Button
                type="button"
                variant="default"
                onClick={resetAndClose}
                disabled={replaceMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                loading={replaceMutation.isPending}
                disabled={Boolean(staleConflict && !reviewedUpdatedAssignment)}
              >
                {replaceMutation.isPending
                  ? "Replacing pathologist"
                  : "Replace pathologist"}
              </Button>
            </Group>
          </Stack>
        </form>
      ) : null}
    </Modal>
  );
}
